export type DashboardTheme = 'light' | 'dark';

export type DatasetStatus = 'ok' | 'loading' | 'error' | 'missing';

export interface DatasetState {
    status: DatasetStatus;
    data: unknown;
    message: string;
    refreshing?: boolean;
    accessDenied?: boolean;
}

export interface ElementPick {
    label: string;
    selector: string;
    html: string;
    source: string;
}

export interface CommentPick extends ElementPick {
    breadcrumb: string;
    rect: { left: number; top: number; width: number; height: number };
}

export type DashboardVariables = Record<string, unknown>;

export type DashboardToHostMessage =
    | { dash: 'ready' }
    | { dash: 'refresh'; id: string }
    | { dash: 'variables'; variables: DashboardVariables }
    | ({ dash: 'pick' } & ElementPick)
    | ({ dash: 'comment' } & CommentPick)
    | { dash: 'tab'; label: string }
    | { dash: 'commentCancel' };

export type HostToDashboardMessage =
    | {
          dash: 'state';
          datasets: Record<string, DatasetState>;
          variables: DashboardVariables;
      }
    | { dash: 'mode'; editing: boolean; commenting?: boolean }
    | { dash: 'commentClear' };

const CONTENT_SECURITY_POLICY = [
    "default-src 'none'",
    "script-src 'unsafe-inline'",
    "style-src 'unsafe-inline' https://fonts.googleapis.com",
    'img-src data:',
    'font-src data: https://fonts.gstatic.com',
    "base-uri 'none'",
    "form-action 'none'"
].join('; ');

const PALETTES: Record<DashboardTheme, Record<string, string>> = {
    light: {
        '--color-fg': '#1f2328',
        '--color-fg-muted': '#656d76',
        '--color-bg': '#ffffff',
        '--color-panel': '#f6f8fa',
        '--color-border-line': '#d0d7de',
        '--color-ok': '#1a7f37',
        '--color-warn': '#9a6700',
        '--color-bad': '#cf222e',
        '--skeleton': 'hsl(0 0% 90%)',
        '--dash-1': '#2f6fb5',
        '--dash-2': '#d9822b',
        '--dash-3': '#3a9a6a',
        '--dash-4': '#8a5cc2',
        '--dash-5': '#c2484f',
        '--dash-6': '#2a9db0',
        '--dash-7': '#a5a031',
        '--dash-8': '#7a7f87'
    },
    dark: {
        '--color-fg': '#e6edf3',
        '--color-fg-muted': '#9198a1',
        '--color-bg': '#0d1117',
        '--color-panel': '#161b22',
        '--color-border-line': '#30363d',
        '--color-ok': '#3fb950',
        '--color-warn': '#d29922',
        '--color-bad': '#f85149',
        '--skeleton': 'hsl(0 1% 26%)',
        '--dash-1': '#5b9bd5',
        '--dash-2': '#f0a04b',
        '--dash-3': '#5cc08c',
        '--dash-4': '#b08be0',
        '--dash-5': '#e5757b',
        '--dash-6': '#55c1d3',
        '--dash-7': '#c9c45a',
        '--dash-8': '#9aa0a8'
    }
};

export const DASH_RUNTIME_SOURCE = `(function () {
var state = null;
var variables = {};
var listeners = [];
function read(id) {
  if (!state) return { status: 'loading', data: [], message: '' };
  return state[id] || { status: 'missing', data: [], message: 'No dataset with this id' };
}
function stamp() {
  var marked = document.querySelectorAll('[data-source]');
  for (var i = 0; i < marked.length; i++) {
    var ids = marked[i].getAttribute('data-source').split(' ');
    var flagged = '';
    var failure = '';
    for (var j = 0; j < ids.length; j++) {
      var source = read(ids[j]);
      if (source.status !== 'ok') {
        flagged = source.status === 'loading' ? 'loading' : 'error';
        failure = source.message || 'The data could not be loaded';
        break;
      }
      if (source.refreshing) flagged = 'refreshing';
    }
    if (flagged) marked[i].setAttribute('data-dash-source-state', flagged);
    else marked[i].removeAttribute('data-dash-source-state');
    if (flagged === 'error') {
      marked[i].setAttribute('title', failure);
      marked[i].setAttribute('data-dash-titled', '');
    } else if (marked[i].hasAttribute('data-dash-titled')) {
      marked[i].removeAttribute('title');
      marked[i].removeAttribute('data-dash-titled');
    }
  }
}
var tabLabel = null;
var ACTIVE_CLASSES = ' active on selected current is-active is-selected ';
function isTab(el) {
  if (el.getAttribute('role') === 'tab') return true;
  for (var i = 0; i < el.classList.length; i++) {
    var name = el.classList[i].toLowerCase();
    if (name === 'tab' || name.slice(-4) === '-tab' || name.slice(-4) === '_tab' || name.indexOf('tab-') === 0 || name.indexOf('tab_') === 0) return true;
  }
  return false;
}
function isActiveTab(el) {
  if (el.getAttribute('aria-selected') === 'true' || el.getAttribute('aria-pressed') === 'true') return true;
  var current = el.getAttribute('aria-current');
  if (current && current !== 'false') return true;
  for (var i = 0; i < el.classList.length; i++) {
    if (ACTIVE_CLASSES.indexOf(' ' + el.classList[i].toLowerCase() + ' ') >= 0) return true;
  }
  return false;
}
function activeTabLabel() {
  var all = document.querySelectorAll('#dash-root [role="tab"], #dash-root button, #dash-root a, #dash-root li');
  var candidates = [];
  for (var i = 0; i < all.length; i++) {
    if (isTab(all[i])) candidates.push(all[i]);
  }
  for (var j = 0; j < candidates.length; j++) {
    var tab = candidates[j];
    if (!isActiveTab(tab)) continue;
    var group = 0;
    for (var k = 0; k < candidates.length; k++) {
      if (candidates[k].parentElement === tab.parentElement) group++;
    }
    if (group > 1) return clip(plainText(tab), 60);
  }
  return '';
}
function reportTab() {
  var label = activeTabLabel();
  if (label === tabLabel) return;
  tabLabel = label;
  parent.postMessage({ dash: 'tab', label: label }, '*');
}
new MutationObserver(reportTab).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-selected', 'aria-pressed', 'aria-current', 'class'] });
function emit() {
  stamp();
  reportTab();
  for (var i = 0; i < listeners.length; i++) {
    try { listeners[i](); } catch (error) { console.error(error); }
  }
}
window.dash = {
  data: read,
  onData: function (fn) {
    listeners.push(fn);
    try { fn(); } catch (error) { console.error(error); }
  },
  refresh: function (id) { parent.postMessage({ dash: 'refresh', id: id }, '*'); },
  setVar: function (name, value) { var values = {}; values[name] = value; parent.postMessage({ dash: 'variables', variables: values }, '*'); },
  setVariables: function (values) { parent.postMessage({ dash: 'variables', variables: values }, '*'); },
  get variables() { return variables; },
  colors: ['var(--dash-1)', 'var(--dash-2)', 'var(--dash-3)', 'var(--dash-4)', 'var(--dash-5)', 'var(--dash-6)', 'var(--dash-7)', 'var(--dash-8)']
};
window.addEventListener('message', function (event) {
  if (event.source !== parent || !event.data) return;
  if (event.data.dash === 'mode') {
    setEditing(!!event.data.editing);
    setCommenting(!!event.data.commenting);
    return;
  }
  if (event.data.dash === 'commentClear') {
    clearPick();
    return;
  }
  if (event.data.dash !== 'state') return;
  state = event.data.datasets;
  variables = event.data.variables || {};
  emit();
});
window.addEventListener('resize', emit);
document.addEventListener('DOMContentLoaded', emit);
var INLINE = /^(span|b|i|em|strong|small|a|code|abbr|sub|sup|tspan|path|circle|rect|line|g|text)$/i;
var hovered = null;
var outline = null;
var editButton = null;
function blockOf(node) {
  var el = node && node.nodeType === 1 ? node : node && node.parentElement;
  var root = document.getElementById('dash-root');
  var svg = el && el.closest('svg');
  if (svg) el = svg;
  while (el && el !== root && INLINE.test(el.tagName)) el = el.parentElement;
  return el && el !== root && root && root.contains(el) ? el : null;
}
var DATA_PART = 'svg,canvas,table';
function dataPartOf(bound) {
  if (bound.matches(DATA_PART)) return bound;
  return bound.querySelector(DATA_PART) || bound;
}
function ownerOf(el) {
  return el.closest('[data-source]') || el;
}
function targetOf(node, altKey) {
  var el = node && node.nodeType === 1 ? node : node && node.parentElement;
  var root = document.getElementById('dash-root');
  var bound = el && el.closest('[data-source]');
  if (bound && root && root.contains(bound)) {
    var part = dataPartOf(bound);
    if (part === bound || part.contains(el)) return part;
    return altKey ? blockOf(node) : null;
  }
  return altKey ? blockOf(node) : null;
}
function kindOf(el) {
  var tag = el.tagName.toLowerCase();
  if (tag === 'table') return 'Table';
  if (tag === 'svg' || tag === 'canvas' || el.querySelector('svg,canvas')) return 'Chart';
  if (/^h[1-6]$/.test(tag)) return 'Heading';
  if (tag === 'p') return 'Text';
  return 'Card';
}
function labelOf(el) {
  var heading = /^h[1-6]$/.test(el.tagName.toLowerCase()) ? el : el.querySelector('h1,h2,h3,h4,h5,h6');
  var text = ((heading || el).textContent || '').split(String.fromCharCode(10)).join(' ').replace(/ +/g, ' ').trim();
  return kindOf(el) + ' · "' + text.slice(0, 40) + (text.length > 40 ? '…' : '') + '"';
}
function selectorOf(el) {
  var root = document.getElementById('dash-root');
  var parts = [];
  while (el && el !== root) {
    var index = Array.prototype.indexOf.call(el.parentElement.children, el) + 1;
    parts.unshift(el.tagName.toLowerCase() + ':nth-child(' + index + ')');
    el = el.parentElement;
  }
  return '#dash-root > ' + parts.join(' > ');
}
function ensureOverlay() {
  if (outline) return;
  outline = document.createElement('div');
  outline.style.cssText = 'position:absolute;pointer-events:none;border:2px solid var(--dash-1);border-radius:4px;z-index:2147483646;display:none';
  editButton = document.createElement('button');
  editButton.type = 'button';
  editButton.textContent = 'Edit element';
  editButton.style.cssText = 'position:absolute;z-index:2147483647;display:none;padding:4px 10px;border:1px solid var(--color-border-line);border-radius:8px;background:var(--color-bg);color:var(--color-fg);font:inherit;font-size:12px;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,.15)';
  editButton.addEventListener('click', function () {
    if (!hovered) return;
    var owner = ownerOf(hovered);
    parent.postMessage({ dash: 'pick', label: labelOf(owner), selector: selectorOf(owner), html: owner.outerHTML.slice(0, 400), source: owner.getAttribute('data-source') || '' }, '*');
  });
  document.body.appendChild(outline);
  document.body.appendChild(editButton);
}
function showOverlay(el) {
  ensureOverlay();
  hovered = el;
  var rect = el.getBoundingClientRect();
  var left = rect.left + window.scrollX;
  var top = rect.top + window.scrollY;
  outline.style.cssText += ';display:block;left:' + left + 'px;top:' + top + 'px;width:' + rect.width + 'px;height:' + rect.height + 'px';
  editButton.style.display = 'block';
  editButton.style.left = Math.max(4, left + rect.width - editButton.offsetWidth - 6) + 'px';
  editButton.style.top = (top + 6) + 'px';
}
var editing = false;
function setEditing(on) {
  editing = on;
  if (on) document.documentElement.setAttribute('data-dash-editing', '');
  else {
    document.documentElement.removeAttribute('data-dash-editing');
    hideOverlay();
  }
}
function hideOverlay() {
  hovered = null;
  if (outline) outline.style.display = 'none';
  if (editButton) editButton.style.display = 'none';
}
var commenting = false;
var picked = null;
var commentOutline = null;
function commentTargetOf(node) {
  var el = node && node.nodeType === 1 ? node : node && node.parentElement;
  var root = document.getElementById('dash-root');
  var svg = el && el.closest('svg');
  if (svg) el = svg;
  return el && el !== root && root && root.contains(el) ? el : null;
}
function commentKind(el) {
  var tag = el.tagName.toLowerCase();
  var role = el.getAttribute('role');
  if (role === 'tab') return 'tab';
  if (tag === 'button' || role === 'button') return 'button';
  if (tag === 'a') return 'link';
  if (/^h[1-6]$/.test(tag)) return 'heading';
  if (tag === 'table') return 'table';
  if (tag === 'svg' || tag === 'canvas') return 'chart';
  if (tag === 'img') return 'image';
  if (/^(input|select|textarea)$/.test(tag)) return 'field';
  if (/^(p|span|b|i|em|strong|small|li|td|th|label)$/.test(tag)) return 'text';
  return 'section';
}
function plainText(el) {
  return (el.getAttribute('aria-label') || el.textContent || '').split(String.fromCharCode(10)).join(' ').replace(/ +/g, ' ').trim();
}
function clip(text, max) {
  return text.length > max ? text.slice(0, max) + '…' : text;
}
function commentLabel(el) {
  var text = clip(plainText(el), 40);
  return text ? commentKind(el) + ' "' + text + '"' : commentKind(el);
}
function commentPath(el) {
  var root = document.getElementById('dash-root');
  var names = [];
  for (var node = el.parentElement; node && node !== root && names.length < 2; node = node.parentElement) {
    var heading = node.querySelector('h1,h2,h3,h4,h5,h6');
    var name = node.getAttribute('aria-label') || (heading && !el.contains(heading) ? plainText(heading) : '');
    name = clip(name, 40);
    if (name && names.indexOf(name) < 0) names.unshift(name);
  }
  return names;
}
function showCommentOutline(el) {
  if (!commentOutline) {
    commentOutline = document.createElement('div');
    document.body.appendChild(commentOutline);
  }
  var rect = el.getBoundingClientRect();
  commentOutline.style.cssText = 'position:fixed;pointer-events:none;border:2px solid var(--dash-1);border-radius:4px;background:color-mix(in srgb,var(--dash-1) 10%,transparent);z-index:2147483646;left:' + rect.left + 'px;top:' + rect.top + 'px;width:' + rect.width + 'px;height:' + rect.height + 'px';
}
function clearPick() {
  picked = null;
  if (commentOutline) commentOutline.style.display = 'none';
}
function setCommenting(on) {
  commenting = on;
  if (on) document.documentElement.setAttribute('data-dash-commenting', '');
  else {
    document.documentElement.removeAttribute('data-dash-commenting');
    clearPick();
  }
}
document.addEventListener('mouseover', function (event) {
  if (!commenting || picked) return;
  var el = commentTargetOf(event.target);
  if (el) showCommentOutline(el);
});
document.addEventListener('click', function (event) {
  if (!commenting) return;
  event.preventDefault();
  event.stopPropagation();
  var el = commentTargetOf(event.target);
  if (!el) return;
  picked = el;
  showCommentOutline(el);
  var rect = el.getBoundingClientRect();
  var bound = el.closest('[data-source]');
  parent.postMessage({
    dash: 'comment',
    label: commentLabel(el),
    breadcrumb: commentPath(el).concat([commentLabel(el)]).join(' › '),
    selector: selectorOf(el),
    html: el.outerHTML.slice(0, 2000),
    source: bound ? bound.getAttribute('data-source') : '',
    rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
  }, '*');
}, true);
window.addEventListener('scroll', function () {
  if (!picked) return;
  clearPick();
  parent.postMessage({ dash: 'commentCancel' }, '*');
}, true);
document.addEventListener('mouseover', function (event) {
  if (!editing || event.target === editButton) return;
  var el = targetOf(event.target, event.altKey);
  if (el && el !== hovered) showOverlay(el);
});
document.documentElement.addEventListener('mouseleave', function () {
  setTimeout(function () { if (!editButton || !editButton.matches(':hover')) hideOverlay(); }, 150);
});
parent.postMessage({ dash: 'ready' }, '*');
})();`;

const variablesOf = (theme: DashboardTheme) =>
    Object.entries(PALETTES[theme])
        .map(([name, value]) => `${name}:${value}`)
        .join(';');

const baseStyles = (theme: DashboardTheme) => `
:root{${variablesOf(theme)};color-scheme:${theme}}
html,body{margin:0;background:var(--color-bg);color:var(--color-fg);font-family:system-ui,-apple-system,'Segoe UI',sans-serif;font-size:14px;line-height:1.45}
*,*::before,*::after{box-sizing:border-box}
#dash-root{padding:16px;min-width:0}
#dash-root svg,#dash-root img,#dash-root canvas{max-width:100%}
[data-dash-source-state="loading"]{color:transparent!important;background:var(--skeleton)!important;border-color:transparent!important;box-shadow:none!important;border-radius:.375rem;pointer-events:none;animation:dash-pulse 2s cubic-bezier(.4,0,.6,1) infinite}
[data-dash-source-state="loading"] *{visibility:hidden}
@keyframes dash-pulse{50%{opacity:.5}}
@media (prefers-reduced-motion:reduce){[data-dash-source-state="loading"]{animation:none}}
[data-dash-source-state="refreshing"]{opacity:.7;transition:opacity .15s}
[data-dash-source-state="error"]{color:var(--color-bad);outline:1px dashed var(--color-bad);outline-offset:2px;border-radius:4px}
[data-dash-commenting],[data-dash-commenting] *{cursor:crosshair!important}
[data-dash-editing] [data-source]:not(:has(svg,canvas,table)),[data-dash-editing] [data-source] :is(svg,canvas,table),[data-dash-editing] :is(svg,canvas,table)[data-source]{background:color-mix(in srgb,var(--dash-1) 12%,transparent);border-radius:4px;cursor:pointer}
`;

export function buildDashboardDocument(
    html: string,
    theme: DashboardTheme
): string {
    return [
        '<!doctype html>',
        `<html data-theme="${theme}"><head>`,
        '<meta charset="utf-8">',
        '<meta name="viewport" content="width=device-width,initial-scale=1">',
        `<meta http-equiv="Content-Security-Policy" content="${CONTENT_SECURITY_POLICY}">`,
        `<style>${baseStyles(theme)}</style>`,
        `<script>${DASH_RUNTIME_SOURCE}</script>`,
        '</head><body>',
        `<div id="dash-root">${html}</div>`,
        '</body></html>'
    ].join('');
}
